import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--color-interface-dark)] text-[var(--color-interface-white)] hover:bg-[var(--color-interface-dark)]",
        secondary:
          "border border-[var(--color-border)] bg-[var(--color-brand-50)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-brand-100)]",
        outline:
          "border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-brand-50)]",
        ghost:
          "text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-brand-50)]",
        link: "text-[var(--color-interface-dark)] underline-offset-4 transition-colors hover:underline",
        destructive:
          "bg-[var(--color-error)] text-[var(--color-interface-white)] shadow-[0_10px_24px_-18px_rgba(220,38,38,0.55)] transition-colors hover:bg-[#b91c1c]",
      },
      size: {
        default: "h-[50px] px-6",
        sm: "h-9 px-4 text-sm",
        lg: "h-14 px-8 text-base",
        icon: "size-[50px]",
        "icon-sm": "size-9",
        "icon-lg": "size-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

function ButtonTextWrapper({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex flex-col h-[1.4em] overflow-hidden pointer-events-none">
      <span className="transition-transform duration-200 ease-out group-hover:-translate-y-[1.4em]">
        {children}
      </span>
      <span className="transition-transform duration-200 ease-out group-hover:-translate-y-[1.4em]">
        {children}
      </span>
    </span>
  )
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  animated = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    animated?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), animated && "group", className)}
      {...props}
    >
      {animated ? <ButtonTextWrapper>{children}</ButtonTextWrapper> : children}
    </Comp>
  )
}

export { Button, ButtonTextWrapper }
